/** REST request/response schemas — the HTTP surface for web + future native app. */
import { z } from "zod";

export const nicknameSchema = z.string().trim().min(1).max(24);

/** A shareable game code — exactly 6 unambiguous uppercase chars. */
export const JOIN_CODE_LENGTH = 6;
export const joinCodeSchema = z
  .string()
  .length(JOIN_CODE_LENGTH)
  .regex(/^[A-Z0-9]+$/);

/** An opaque game id (also the Durable Object name) — bounded to sane lengths. */
export const gameIdSchema = z
  .string()
  .min(1)
  .max(32)
  .regex(/^[a-z0-9]+$/);

export const createSessionSchema = z.object({
  nickname: nicknameSchema,
});
export type CreateSessionRequest = z.infer<typeof createSessionSchema>;

export const sessionResponseSchema = z.object({
  token: z.string(),
  playerId: z.string(),
  nickname: nicknameSchema,
});
export type SessionResponse = z.infer<typeof sessionResponseSchema>;

export const updateSessionSchema = z.object({
  nickname: nicknameSchema,
});

export const createGameSchema = z.object({
  visibility: z.enum(["public", "private"]),
  /** 4-digit PIN, required client-side for private games. */
  pin: z
    .string()
    .regex(/^\d{4}$/)
    .optional(),
});
export type CreateGameRequest = z.infer<typeof createGameSchema>;

export const gameSummarySchema = z.object({
  id: z.string(),
  joinCode: z.string(),
  visibility: z.enum(["public", "private"]),
  status: z.enum(["waiting", "ready", "active", "finished", "abandoned"]),
  hostNickname: z.string(),
  playerCount: z.number(),
  createdAt: z.number(),
});
export type GameSummary = z.infer<typeof gameSummarySchema>;

export const createGameResponseSchema = z.object({
  id: z.string(),
  joinCode: z.string(),
  visibility: z.enum(["public", "private"]),
});
export type CreateGameResponse = z.infer<typeof createGameResponseSchema>;

export const practiceGameResponseSchema = z.object({
  id: z.string(),
});
export type PracticeGameResponse = z.infer<typeof practiceGameResponseSchema>;

export const joinGameSchema = z.object({
  pin: z
    .string()
    .regex(/^\d{4}$/)
    .optional(),
});

export const publicGamesResponseSchema = z.object({
  games: z.array(gameSummarySchema),
});

export const errorResponseSchema = z.object({
  error: z.string(),
  message: z.string().optional(),
});
